import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from "@mui/material";
import { DataImportCellError, IDataImportField } from "../DataImport.interfaces";
import { formatValue } from "../helpers/DataInporter.helpers";

interface DataPreviewTableProps<TEntity extends Record<string, unknown>> {
  fields: IDataImportField[];
  rows: TEntity[];
  errors: DataImportCellError[];
  page: number;
  rowsCount: number;
  maxPreviewRows: number;
  onPageChange: (page: number) => void;
}

export function DataPreviewTable<TEntity extends Record<string, unknown>>({
  fields,
  rows,
  errors,
  page,
  rowsCount,
  maxPreviewRows,
  onPageChange,
}: DataPreviewTableProps<TEntity>) {
  if (rows.length === 0) return null;

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
      <Box sx={{ p: 1, borderBottom: 1, borderColor: "divider", bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)" }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, pl: 0.5 }}>
          Data Preview
        </Typography>
      </Box>
      <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: 40, fontSize: "0.75rem" }}>#</TableCell>
            {fields.map((field) => (
              <TableCell key={field.key} sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.75rem" }}>
                {field.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, localIndex) => {
            const absoluteRowIndex = page * maxPreviewRows + localIndex;
            const rowErrors = errors.filter((error) => error.rowIndex === absoluteRowIndex);
            const hasRowError = rowErrors.length > 0;

            return (
              <TableRow key={absoluteRowIndex} hover sx={{ bgcolor: hasRowError ? "error.lighter" : "inherit" }}>
                <TableCell sx={{ color: "text.disabled", fontSize: "0.75rem" }}>{absoluteRowIndex + 1}</TableCell>
                {fields.map((field) => {
                  const cellErrors = rowErrors.filter((error) => error.fieldKey === field.key);
                  const hasCellError = cellErrors.length > 0;

                  return (
                    <Tooltip key={field.key} title={hasCellError ? cellErrors[0].message : ""} placement="top" arrow>
                      <TableCell
                        sx={{
                          borderLeft: hasCellError ? "2px solid" : "2px solid transparent",
                          borderColor: hasCellError ? "error.main" : "transparent",
                          color: hasCellError ? "error.main" : "text.primary",
                          fontWeight: hasCellError ? 600 : 400,
                          fontSize: "0.8125rem",
                        }}
                      >
                        {formatValue(row[field.key])}
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={rowsCount}
        page={page}
        rowsPerPage={maxPreviewRows}
        rowsPerPageOptions={[maxPreviewRows]}
        onPageChange={(_, nextPage) => onPageChange(nextPage)}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
        sx={{ borderTop: 1, borderColor: "divider", minHeight: 36, "& .MuiTablePagination-toolbar": { minHeight: 36 } }}
      />
    </TableContainer>
  );
}
